import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth } from 'firebase/auth';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { AdminSectionHeader } from './AdminSectionHeader';
import { AdminToggle } from './AdminToggle';
import { db } from '../../config/firebase';
import { APP_VERSION, getDeployTarget } from '../../config/environment';
import { useWhatsNew } from '../../contexts/WhatsNewContext';
import {
  buildDefaultRelease,
  buildReleaseId,
  CHANGELOG_AUDIENCE_ADMIN,
  CHANGELOG_AUDIENCE_ALL,
  CHANGELOG_DEPLOY_BOTH,
  CHANGELOG_DEPLOY_PRODUCTION,
  CHANGELOG_DEPLOY_STAGING,
  compareVersions,
  ensureDefaultChangelog,
  normalizeChangelogCard,
  normalizeChangelogConfig,
  normalizeChangelogRelease,
  releasesMatch,
  resolveActiveRelease,
  saveChangelogConfig,
  subscribeChangelogConfig,
} from '../../utils/changelogService';

const ROUTE_OPTIONS = [
  { label: 'My Calendar', value: 'MyCalendar' },
  { label: 'Tour Planner', value: 'TourPlanner' },
  { label: 'Community', value: 'CommunityList' },
  { label: 'Profile', value: 'Profile' },
  { label: 'Venue list', value: 'VenueList' },
  { label: 'Home', value: 'Home' },
];

const DEPLOY_OPTIONS = [
  { label: 'Production & staging', value: CHANGELOG_DEPLOY_BOTH },
  { label: 'Production only', value: CHANGELOG_DEPLOY_PRODUCTION },
  { label: 'Staging only', value: CHANGELOG_DEPLOY_STAGING },
];

const ICON_OPTIONS = [
  'sparkles-outline',
  'calendar-outline',
  'link-outline',
  'settings-outline',
  'notifications-outline',
  'document-text-outline',
  'people-outline',
  'map-outline',
  'heart-outline',
  'rocket-outline',
];

function emptyCard() {
  return normalizeChangelogCard({
    title: '',
    body: '',
    showCta: false,
    ctaLabel: '',
    ctaRoute: '',
    icon: 'sparkles-outline',
  });
}

function applyReleaseIdentity(release, patch = {}) {
  const audience = patch.audience ?? release.audience;
  const deployTarget = patch.deployTarget ?? release.deployTarget;
  return {
    ...release,
    ...patch,
    audience,
    deployTarget,
    id: buildReleaseId({ version: release.version, audience, deployTarget }),
  };
}

function deployBadgeLabel(deployTarget) {
  if (deployTarget === CHANGELOG_DEPLOY_STAGING) return 'STAGING';
  if (deployTarget === CHANGELOG_DEPLOY_PRODUCTION) return 'PROD';
  return null;
}

export function AdminChangeLogs({ onNotify }) {
  const { colors, styles: adminStyles } = useAdminThemeContext();
  const formInputStyle = adminStyles?.formInput ?? {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    fontSize: 16,
  };
  const { setPreviewRelease } = useWhatsNew();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedReleaseId, setSelectedReleaseId] = useState('');
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    return subscribeChangelogConfig(db, (next) => {
      setConfig(next);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!config?.releases?.length) {
      setDraft(null);
      setSelectedReleaseId('');
      return;
    }
    const releaseId =
      selectedReleaseId && config.releases.some((r) => r.id === selectedReleaseId)
        ? selectedReleaseId
        : config.releases[0].id;
    setSelectedReleaseId(releaseId);
    const release = config.releases.find((r) => r.id === releaseId);
    setDraft(release ? { ...release, cards: release.cards.map((c) => ({ ...c })) } : null);
  }, [config, selectedReleaseId]);

  const persist = useCallback(
    async (nextConfig, message = 'Change log saved.') => {
      try {
        setSaving(true);
        const uid = getAuth().currentUser?.uid || null;
        const saved = await saveChangelogConfig(db, nextConfig, uid);
        setConfig(saved);
        onNotify?.(message, 'success');
      } catch (err) {
        onNotify?.(err?.message || 'Could not save change log.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [onNotify]
  );

  const saveDraft = useCallback(async () => {
    if (!draft || !config) return;
    const normalized = normalizeChangelogRelease(draft);
    const releases = config.releases.map((r) => (releasesMatch(r, normalized) ? normalized : r));
    if (!releases.some((r) => releasesMatch(r, normalized))) {
      releases.push(normalized);
    }
    await persist(
      normalizeChangelogConfig({
        ...config,
        releases: releases.sort((a, b) => compareVersions(b.version, a.version)),
      })
    );
  }, [draft, config, persist]);

  const updateGlobal = useCallback(
    (patch) => {
      if (!config) return;
      const next = normalizeChangelogConfig({ ...config, ...patch });
      setConfig(next);
      persist(next, 'Settings updated.');
    },
    [config, persist]
  );

  const addRelease = useCallback(
    async (audience = CHANGELOG_AUDIENCE_ALL) => {
      const version = APP_VERSION || '1.5.6';
      const existing = config?.releases?.find(
        (r) =>
          r.version === version &&
          r.audience === audience &&
          (r.deployTarget || CHANGELOG_DEPLOY_BOTH) === CHANGELOG_DEPLOY_BOTH
      );
      if (existing) {
        setSelectedReleaseId(existing.id);
        const label = audience === CHANGELOG_AUDIENCE_ADMIN ? 'admin-only ' : '';
        onNotify?.(`Release v${version} (${label}audience) already exists — selected it.`, 'success');
        return;
      }
      const release =
        audience === CHANGELOG_AUDIENCE_ALL
          ? buildDefaultRelease(version)
          : normalizeChangelogRelease({
              version,
              audience: CHANGELOG_AUDIENCE_ADMIN,
              title: `Admin notes for v${version}`,
              subtitle: 'Internal updates and admin-only changes.',
              published: false,
              cards: [emptyCard()],
            });
      const next = normalizeChangelogConfig({
        ...(config || {}),
        enabled: true,
        linkToLatest: true,
        releases: [...(config?.releases || []), release],
      });
      await persist(
        next,
        audience === CHANGELOG_AUDIENCE_ADMIN
          ? `Created admin-only release v${version}.`
          : `Created release v${version}.`
      );
      setSelectedReleaseId(release.id);
    },
    [config, persist, onNotify]
  );

  const seedIfEmpty = useCallback(async () => {
    try {
      setSaving(true);
      const uid = getAuth().currentUser?.uid || null;
      const seeded = await ensureDefaultChangelog(db, uid);
      setConfig(seeded);
      setSelectedReleaseId(seeded.releases[0]?.id || '');
      onNotify?.('Default change log loaded.', 'success');
    } catch (err) {
      onNotify?.(err?.message || 'Could not seed change log.', 'error');
    } finally {
      setSaving(false);
    }
  }, [onNotify]);

  const updateCard = (cardId, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cards: prev.cards.map((c) => (c.id === cardId ? { ...c, ...patch } : c)),
      };
    });
  };

  const addCard = () => {
    setDraft((prev) => (prev ? { ...prev, cards: [...prev.cards, emptyCard()] } : prev));
  };

  const removeCard = (cardId) => {
    setDraft((prev) =>
      prev ? { ...prev, cards: prev.cards.filter((c) => c.id !== cardId) } : prev
    );
  };

  const moveCard = (cardId, direction) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const idx = prev.cards.findIndex((c) => c.id === cardId);
      if (idx < 0) return prev;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= prev.cards.length) return prev;
      const cards = [...prev.cards];
      const [item] = cards.splice(idx, 1);
      cards.splice(nextIdx, 0, item);
      return { ...prev, cards };
    });
  };

  const previewCurrent = () => {
    if (!draft) return;
    setPreviewRelease(normalizeChangelogRelease(draft));
  };

  const activeVersionLabel = useMemo(() => {
    if (!config) return '—';
    const version = config.linkToLatest ? APP_VERSION || '?' : config.pinnedVersion || '—';
    if (version === '—') return version;
    const deployTarget = getDeployTarget();
    const allRelease = resolveActiveRelease(config, APP_VERSION, { isAdmin: false, deployTarget });
    const adminRelease = resolveActiveRelease(config, APP_VERSION, { isAdmin: true, deployTarget });
    const envLabel =
      deployTarget === CHANGELOG_DEPLOY_PRODUCTION
        ? 'production'
        : deployTarget === CHANGELOG_DEPLOY_STAGING
          ? 'staging'
          : 'local/staging';
    const parts = [];
    if (allRelease) parts.push(`Everyone → v${allRelease.version}`);
    if (
      adminRelease &&
      adminRelease.audience === CHANGELOG_AUDIENCE_ADMIN &&
      adminRelease.id !== allRelease?.id
    ) {
      parts.push(`Admins → v${adminRelease.version} (admin)`);
    } else if (adminRelease && !allRelease) {
      parts.push(`Admins → v${adminRelease.version}`);
    }
    if (!parts.length) return `v${version} (no matching published release on ${envLabel})`;
    return `${parts.join(' · ')} · ${envLabel}`;
  }, [config]);

  if (loading) {
    return (
      <View style={{ paddingVertical: 40, alignItems: 'center' }}>
        <ActivityIndicator color={colors.navActive} />
      </View>
    );
  }

  const hasReleases = Boolean(config?.releases?.length);

  return (
    <View style={{ flex: 1 }}>
      <AdminSectionHeader
        isFirst
        title="What's New modal"
        subtitle="Configure the release notes modal users see once per version. Cards can link anywhere in the app."
      />

      {!hasReleases ? (
        <View
          style={{
            padding: 20,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            backgroundColor: colors.cardBg,
            marginBottom: 20,
          }}
        >
          <Text style={{ color: colors.textPrimary, fontWeight: '700', marginBottom: 8 }}>
            No change logs yet
          </Text>
          <Text style={{ color: colors.textSecondary, marginBottom: 16, lineHeight: 20 }}>
            Load the v{APP_VERSION || '1.5.6'} template with five cards covering recent calendar and sharing updates.
          </Text>
          <TouchableOpacity
            onPress={seedIfEmpty}
            disabled={saving}
            style={{
              alignSelf: 'flex-start',
              paddingHorizontal: 16,
              paddingVertical: 10,
              borderRadius: 8,
              backgroundColor: colors.navActive,
            }}
          >
            <Text style={{ color: colors.textOnAccent, fontWeight: '800' }}>Load default for v{APP_VERSION}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {hasReleases ? (
        <>
          <View
            style={{
              padding: 16,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: colors.cardBorder,
              backgroundColor: colors.cardBg,
              marginBottom: 20,
              gap: 14,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Modal enabled</Text>
                <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                  When off, nothing shows regardless of version.
                </Text>
              </View>
              <AdminToggle
                value={config.enabled}
                onValueChange={(enabled) => updateGlobal({ enabled })}
                activeColor={colors.navActive}
              />
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Link to latest version</Text>
                <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                  On: shows only when app version matches a published release. Off: pin to a specific version below.
                </Text>
              </View>
              <AdminToggle
                value={config.linkToLatest}
                onValueChange={(linkToLatest) => updateGlobal({ linkToLatest })}
                activeColor={colors.navActive}
              />
            </View>

            {!config.linkToLatest ? (
              <View>
                <Text style={{ color: colors.textMuted, fontSize: 11, fontWeight: '700', marginBottom: 6 }}>
                  PINNED VERSION
                </Text>
                <TextInput
                  value={config.pinnedVersion}
                  onChangeText={(pinnedVersion) => setConfig((c) => ({ ...c, pinnedVersion }))}
                  onBlur={() => updateGlobal({ pinnedVersion: config.pinnedVersion })}
                  style={[formInputStyle, { marginBottom: 0 }]}
                  placeholder="1.5.6"
                  placeholderTextColor={colors.inputPlaceholder}
                />
              </View>
            ) : null}

            <Text style={{ color: colors.textMuted, fontSize: 12 }}>
              Active for users: <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>{activeVersionLabel}</Text>
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
              Use <Text style={{ fontWeight: '700', color: colors.textPrimary }}>Admins only</Text> on a release to show
              that modal only to admin accounts — everyone else keeps the standard release for the same version.
            </Text>
          </View>

          <View style={{ flexDirection: Platform.OS === 'web' ? 'row' : 'column', gap: 20, alignItems: 'flex-start' }}>
            <View style={{ flex: 1, minWidth: 0, width: '100%' }}>
              <AdminSectionHeader
                title="Releases"
                subtitle="Each release is shown once per user. You can publish a public release and a separate admin-only release for the same version."
              />

              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {config.releases.map((r) => {
                    const active = r.id === selectedReleaseId;
                    const isAdminRelease = r.audience === CHANGELOG_AUDIENCE_ADMIN;
                    const deployBadge = deployBadgeLabel(r.deployTarget);
                    return (
                      <TouchableOpacity
                        key={r.id}
                        onPress={() => setSelectedReleaseId(r.id)}
                        style={{
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: active ? colors.navActive : colors.cardBorder,
                          backgroundColor: active ? `${colors.navActive}22` : colors.cardBgSubtle,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Text style={{ color: active ? colors.navActive : colors.textPrimary, fontWeight: '700' }}>
                          v{r.version}
                        </Text>
                        {isAdminRelease ? (
                          <View
                            style={{
                              paddingHorizontal: 6,
                              paddingVertical: 2,
                              borderRadius: 4,
                              backgroundColor: active ? `${colors.navActive}44` : `${colors.textMuted}33`,
                            }}
                          >
                            <Text
                              style={{
                                color: active ? colors.navActive : colors.textSecondary,
                                fontSize: 10,
                                fontWeight: '800',
                              }}
                            >
                              ADMIN
                            </Text>
                          </View>
                        ) : null}
                        {deployBadge ? (
                          <View
                            style={{
                              paddingHorizontal: 6,
                              paddingVertical: 2,
                              borderRadius: 4,
                              backgroundColor: active ? `${colors.navActive}44` : `${colors.textMuted}33`,
                            }}
                          >
                            <Text
                              style={{
                                color: active ? colors.navActive : colors.textSecondary,
                                fontSize: 10,
                                fontWeight: '800',
                              }}
                            >
                              {deployBadge}
                            </Text>
                          </View>
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity
                    onPress={() => addRelease(CHANGELOG_AUDIENCE_ALL)}
                    style={{
                      paddingHorizontal: 14,
                      paddingVertical: 8,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: colors.cardBorder,
                    }}
                  >
                    <Text style={{ color: colors.textSecondary, fontWeight: '700' }}>+ Public</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => addRelease(CHANGELOG_AUDIENCE_ADMIN)}
                    style={{
                      paddingHorizontal: 14,
                      paddingVertical: 8,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: colors.cardBorder,
                    }}
                  >
                    <Text style={{ color: colors.textSecondary, fontWeight: '700' }}>+ Admin</Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>

              {draft ? (
                <View style={{ gap: 12 }}>
                  <TextInput
                    value={draft.title}
                    onChangeText={(title) => setDraft((p) => ({ ...p, title }))}
                    style={formInputStyle}
                    placeholder="Modal title"
                    placeholderTextColor={colors.inputPlaceholder}
                  />
                  <TextInput
                    value={draft.subtitle}
                    onChangeText={(subtitle) => setDraft((p) => ({ ...p, subtitle }))}
                    style={formInputStyle}
                    placeholder="Subtitle"
                    placeholderTextColor={colors.inputPlaceholder}
                  />

                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flex: 1, paddingRight: 12 }}>
                      <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Audience</Text>
                      <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                        Admins only hides this release from non-admin users.
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Text
                        style={{
                          color:
                            draft.audience === CHANGELOG_AUDIENCE_ADMIN
                              ? colors.textMuted
                              : colors.textPrimary,
                          fontWeight: '700',
                          fontSize: 12,
                        }}
                      >
                        Everyone
                      </Text>
                      <AdminToggle
                        value={draft.audience === CHANGELOG_AUDIENCE_ADMIN}
                        onValueChange={(adminOnly) =>
                          setDraft((p) =>
                            applyReleaseIdentity(p, {
                              audience: adminOnly ? CHANGELOG_AUDIENCE_ADMIN : CHANGELOG_AUDIENCE_ALL,
                            })
                          )
                        }
                        activeColor={colors.navActive}
                      />
                      <Text
                        style={{
                          color:
                            draft.audience === CHANGELOG_AUDIENCE_ADMIN
                              ? colors.textPrimary
                              : colors.textMuted,
                          fontWeight: '700',
                          fontSize: 12,
                        }}
                      >
                        Admins only
                      </Text>
                    </View>
                  </View>

                  <View>
                    <Text style={{ color: colors.textPrimary, fontWeight: '700', marginBottom: 6 }}>
                      Show on
                    </Text>
                    <Text style={{ color: colors.textSecondary, fontSize: 12, marginBottom: 10 }}>
                      Limit this release to production, staging, or both. Local dev matches staging.
                    </Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        {DEPLOY_OPTIONS.map((opt) => {
                          const active = (draft.deployTarget || CHANGELOG_DEPLOY_BOTH) === opt.value;
                          return (
                            <TouchableOpacity
                              key={opt.value}
                              onPress={() =>
                                setDraft((p) => applyReleaseIdentity(p, { deployTarget: opt.value }))
                              }
                              style={{
                                paddingHorizontal: 12,
                                paddingVertical: 8,
                                borderRadius: 999,
                                borderWidth: 1,
                                borderColor: active ? colors.navActive : colors.cardBorder,
                                backgroundColor: active ? `${colors.navActive}22` : colors.cardBgSubtle,
                              }}
                            >
                              <Text
                                style={{
                                  color: active ? colors.navActive : colors.textSecondary,
                                  fontWeight: '700',
                                  fontSize: 12,
                                }}
                              >
                                {opt.label}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </ScrollView>
                  </View>

                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Published</Text>
                    <AdminToggle
                      value={draft.published !== false}
                      onValueChange={(published) => setDraft((p) => ({ ...p, published }))}
                      activeColor={colors.navActive}
                    />
                  </View>

                  <AdminSectionHeader title="Cards" subtitle="Each card appears in the modal with an optional Go button." />

                  {draft.cards.map((card, idx) => (
                    <View
                      key={card.id}
                      style={{
                        padding: 14,
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: colors.cardBorder,
                        backgroundColor: colors.cardBgSubtle,
                        gap: 10,
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text style={{ color: colors.textMuted, fontWeight: '700' }}>Card {idx + 1}</Text>
                        <View style={{ flexDirection: 'row', gap: 4 }}>
                          <TouchableOpacity onPress={() => moveCard(card.id, -1)} disabled={idx === 0} hitSlop={8}>
                            <Ionicons name="chevron-up" size={18} color={colors.textSecondary} />
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => moveCard(card.id, 1)}
                            disabled={idx === draft.cards.length - 1}
                            hitSlop={8}
                          >
                            <Ionicons name="chevron-down" size={18} color={colors.textSecondary} />
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => removeCard(card.id)} hitSlop={8}>
                            <Ionicons name="trash-outline" size={18} color="#e74c3c" />
                          </TouchableOpacity>
                        </View>
                      </View>

                      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <View style={{ flexDirection: 'row', gap: 6 }}>
                          {ICON_OPTIONS.map((icon) => (
                            <TouchableOpacity
                              key={icon}
                              onPress={() => updateCard(card.id, { icon })}
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: card.icon === icon ? colors.navActive : colors.cardBorder,
                                backgroundColor: card.icon === icon ? `${colors.navActive}18` : 'transparent',
                              }}
                            >
                              <Ionicons
                                name={icon}
                                size={16}
                                color={card.icon === icon ? colors.navActive : colors.textSecondary}
                              />
                            </TouchableOpacity>
                          ))}
                        </View>
                      </ScrollView>

                      <TextInput
                        value={card.title}
                        onChangeText={(title) => updateCard(card.id, { title })}
                        style={[formInputStyle, { marginBottom: 0 }]}
                        placeholder="Title"
                        placeholderTextColor={colors.inputPlaceholder}
                      />
                      <TextInput
                        value={card.body}
                        onChangeText={(body) => updateCard(card.id, { body })}
                        style={[formInputStyle, { marginBottom: 0, minHeight: 72 }]}
                        placeholder="Description"
                        placeholderTextColor={colors.inputPlaceholder}
                        multiline
                      />

                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <View style={{ flex: 1, paddingRight: 12 }}>
                          <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Show button</Text>
                          <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                            Turn off for text-only cards with no action.
                          </Text>
                        </View>
                        <AdminToggle
                          value={card.showCta !== false}
                          onValueChange={(showCta) =>
                            updateCard(card.id, {
                              showCta,
                              ...(showCta && !card.ctaRoute ? { ctaRoute: 'MyCalendar' } : {}),
                              ...(!showCta ? { ctaRoute: '' } : {}),
                            })
                          }
                          activeColor={colors.navActive}
                        />
                      </View>

                      {card.showCta !== false ? (
                        <>
                          <TextInput
                            value={card.ctaLabel}
                            onChangeText={(ctaLabel) => updateCard(card.id, { ctaLabel })}
                            style={[formInputStyle, { marginBottom: 0 }]}
                            placeholder="Button label (e.g. Open calendar)"
                            placeholderTextColor={colors.inputPlaceholder}
                          />
                          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                            <View style={{ flexDirection: 'row', gap: 6 }}>
                              <TouchableOpacity
                                onPress={() => updateCard(card.id, { ctaRoute: '', showCta: false })}
                                style={{
                                  paddingHorizontal: 10,
                                  paddingVertical: 6,
                                  borderRadius: 999,
                                  borderWidth: 1,
                                  borderColor:
                                    !card.ctaRoute && card.showCta === false
                                      ? colors.navActive
                                      : colors.cardBorder,
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 11,
                                    fontWeight: '700',
                                    color:
                                      !card.ctaRoute && card.showCta === false
                                        ? colors.navActive
                                        : colors.textSecondary,
                                  }}
                                >
                                  None
                                </Text>
                              </TouchableOpacity>
                              {ROUTE_OPTIONS.map((opt) => (
                                <TouchableOpacity
                                  key={opt.value}
                                  onPress={() => updateCard(card.id, { ctaRoute: opt.value, showCta: true })}
                                  style={{
                                    paddingHorizontal: 10,
                                    paddingVertical: 6,
                                    borderRadius: 999,
                                    borderWidth: 1,
                                    borderColor: card.ctaRoute === opt.value ? colors.navActive : colors.cardBorder,
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 11,
                                      fontWeight: '700',
                                      color: card.ctaRoute === opt.value ? colors.navActive : colors.textSecondary,
                                    }}
                                  >
                                    {opt.label}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </ScrollView>
                        </>
                      ) : null}
                    </View>
                  ))}

                  <TouchableOpacity
                    onPress={addCard}
                    style={{
                      paddingVertical: 10,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: colors.cardBorder,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: colors.textSecondary, fontWeight: '700' }}>+ Add card</Text>
                  </TouchableOpacity>

                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                    <TouchableOpacity
                      onPress={saveDraft}
                      disabled={saving}
                      style={{
                        paddingHorizontal: 18,
                        paddingVertical: 11,
                        borderRadius: 8,
                        backgroundColor: colors.navActive,
                      }}
                    >
                      <Text style={{ color: colors.textOnAccent, fontWeight: '800' }}>
                        {saving ? 'Saving…' : 'Save release'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={previewCurrent}
                      style={{
                        paddingHorizontal: 18,
                        paddingVertical: 11,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: colors.cardBorder,
                        backgroundColor: colors.cardBg,
                      }}
                    >
                      <Text style={{ color: colors.textPrimary, fontWeight: '800' }}>Preview modal</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : null}
            </View>
          </View>
        </>
      ) : null}
    </View>
  );
}
